import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Fingerprint, Lock } from 'lucide-react';
import { isBiometricEnabled, verifyBiometric } from '../lib/biometric';
import { getAuthToken } from '../lib/authToken';

// Full-screen lock overlay shown on cold start / resume when the user has
// enabled the biometric lock and a session token is present. Native-only —
// renders nothing on web.
const BiometricGate = () => {
  const [locked, setLocked] = useState(false);
  const busy = useRef(false);

  const isNative = (() => {
    try { return !!Capacitor?.isNativePlatform?.(); } catch { return false; }
  })();

  const tryUnlock = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const ok = await verifyBiometric('Unlock Aayi');
    busy.current = false;
    if (ok) setLocked(false);
  }, []);

  const maybeLock = useCallback(async () => {
    if (!isNative) return;
    if (!getAuthToken()) return;            // not logged in — nothing to protect
    if (!(await isBiometricEnabled())) return;
    setLocked(true);
    tryUnlock();
  }, [isNative, tryUnlock]);

  useEffect(() => {
    if (!isNative) return;
    maybeLock(); // cold start

    let remove = () => {};
    let cancelled = false;
    import('@capacitor/app').then(({ App }) => {
      if (cancelled) return;
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) maybeLock();
      }).then((h) => { remove = () => h.remove(); });
    }).catch(() => {});
    return () => { cancelled = true; remove(); };
  }, [isNative, maybeLock]);

  if (!locked) return null;

  return (
    <div
      data-testid="biometric-lock-overlay"
      className="fixed inset-0 z-[9999] bg-[#FAF7F2] flex flex-col items-center justify-center px-8"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#E05A47] to-[#F4B333] flex items-center justify-center shadow-[0_8px_24px_rgba(224,90,71,0.3)] mb-6">
        <Lock className="w-9 h-9 text-white" />
      </div>
      <h2 className="text-2xl font-black text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>Aayi is locked</h2>
      <p className="text-sm text-[#57534E] text-center mb-8">Unlock with biometrics to continue.</p>
      <button
        data-testid="biometric-unlock-btn"
        onClick={tryUnlock}
        className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1C1917] text-white font-bold"
      >
        <Fingerprint className="w-5 h-5" /> Unlock
      </button>
    </div>
  );
};

export default BiometricGate;
